import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import RegionPicker from '../components/RegionPicker';
import { toast } from '../utils/toast';

/**
 * Pricing Component Masters — full-page editor for the rate history,
 * now split into three priority levels:
 *
 *   L1 — schema + component + jurisdiction(location region) + customer region
 *   L2 — schema + component + customer region (jurisdiction implicit = company region)
 *   L3 — schema + component (no region keys)
 *
 * Three tabs across the top, each backed by its own table on the server.
 * The resolver tries L1 → L2 → L3 strictly in order — the first level
 * with a matching row wins.
 *
 * Two route modes (same as before):
 *   - #/pricing-schemas/component-masters         → all schemas
 *   - #/pricing-schemas/:schemaId/component-masters → schema-locked
 */
export default function PricingComponentMasters() {
  const lockedSchemaId = parseLockedSchemaId();

  const [level, setLevel]           = useState(1); // 1 | 2 | 3
  const [schemas, setSchemas]       = useState([]);
  const [components, setComponents] = useState([]);
  const [rates, setRates]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [draft, setDraft]           = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, ratesRes] = await Promise.all([
        apiFetch('/api/pricing-schemas'),
        apiFetch(lockedSchemaId
          ? `/api/pricing-schemas/${lockedSchemaId}/rates?level=${level}`
          : `/api/pricing-schemas/rates/all?level=${level}`),
      ]);
      if (sRes.ok)     setSchemas((await sRes.json()).schemas || []);
      if (ratesRes.ok) setRates((await ratesRes.json()).rates  || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [lockedSchemaId, level]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Components for the schema selected in the draft form (lazy load).
  const fetchComponents = useCallback(async (schemaId) => {
    if (!schemaId) { setComponents([]); return; }
    try {
      const r = await apiFetch(`/api/pricing-schemas/${schemaId}`);
      if (r.ok) {
        const d = await r.json();
        setComponents(d.components || []);
      }
    } catch (_) { setComponents([]); }
  }, []);

  function startNew() {
    const today   = new Date().toISOString().slice(0, 10);
    const endOfYr = `${new Date().getFullYear()}-12-31`;
    const schemaId = lockedSchemaId || '';
    setDraft({
      _new: true, id: null, level,
      schema_id: schemaId,
      component_id: '',
      jurisdiction_region_id: '',
      customer_region_id:     '',
      from_date: today, to_date: endOfYr,
      rate_pct: '', notes: '',
    });
    fetchComponents(schemaId);
  }
  async function startEdit(r) {
    setDraft({
      _new: false, id: r.id, level,
      schema_id:    r.schema_id || '',
      component_id: r.component_id || '',
      jurisdiction_region_id: r.jurisdiction_region_id || '',
      customer_region_id:     r.customer_region_id     || '',
      from_date: r.from_date ? String(r.from_date).slice(0, 10) : '',
      to_date:   r.to_date   ? String(r.to_date).slice(0, 10)   : '',
      rate_pct:  r.rate_pct ?? '',
      notes:     r.notes || '',
    });
    fetchComponents(r.schema_id);
  }
  function cancelDraft() { setDraft(null); }

  function set(k, v) {
    setDraft(prev => {
      const next = { ...prev, [k]: v };
      if (k === 'schema_id' && v !== prev.schema_id) {
        next.component_id = '';
        fetchComponents(v);
      }
      return next;
    });
  }

  async function save() {
    if (!draft) return;
    if (!draft.schema_id)    { toast.error('Schema is required'); return; }
    if (!draft.component_id) { toast.error('Component is required'); return; }
    if (draft.rate_pct === '' || draft.rate_pct === null) { toast.error('Rate % is required'); return; }
    if (!draft.from_date)    { toast.error('From date is required'); return; }
    if (level === 1) {
      if (!draft.jurisdiction_region_id) { toast.error('L1 requires Jurisdiction'); return; }
      if (!draft.customer_region_id)     { toast.error('L1 requires Customer Region'); return; }
    }
    if (level === 2 && !draft.customer_region_id) { toast.error('L2 requires Customer Region'); return; }

    const body = { ...draft, level };
    const url = draft._new
      ? `/api/pricing-schemas/${draft.schema_id}/rates`
      : `/api/pricing-schemas/${draft.schema_id}/rates/${draft.id}?level=${level}`;
    const method = draft._new ? 'POST' : 'PUT';
    const r = await apiFetch(url, { method, body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success(`L${level} rate saved`); setDraft(null); fetchAll(); }
    else      { toast.error(d.error || 'Save failed'); }
  }

  async function remove(rate) {
    const ok = await toast.confirm({
      title: 'Delete rate?',
      body: `Remove this L${level} rate row for ${rate.component_name || 'component'}?`,
      danger: true,
    });
    if (!ok) return;
    const r = await apiFetch(`/api/pricing-schemas/${rate.schema_id}/rates/${rate.id}?level=${level}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Rate deleted'); fetchAll(); }
    else      { toast.error('Delete failed'); }
  }

  const lockedSchema = useMemo(
    () => lockedSchemaId ? (schemas.find(s => s.id === lockedSchemaId) || null) : null,
    [schemas, lockedSchemaId]
  );

  const filteredRates = useMemo(() => {
    if (!search) return rates;
    const s = search.toLowerCase();
    return rates.filter(r =>
      (r.schema_name||'').toLowerCase().includes(s)    ||
      (r.component_name||'').toLowerCase().includes(s) ||
      (r.jurisdiction_region_name||'').toLowerCase().includes(s) ||
      (r.customer_region_name||'').toLowerCase().includes(s)
    );
  }, [rates, search]);

  return (
    <div style={{ padding: 20 }}>
      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={() => { window.location.hash = '#/pricing-schemas'; }} style={btnGhost}>← Back</button>
        <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>
          Pricing Component Masters
          {lockedSchema && (
            <span style={{ marginLeft: 10, fontSize: 13, color: '#94A3B8', fontWeight: 400 }}>
              · {lockedSchema.code} — {lockedSchema.name}
            </span>
          )}
        </h2>
        <input type="text" placeholder="Search schema, component, region..."
          value={search} onChange={e => setSearch(e.target.value)} style={{ ...inp, width: 320 }} />
        <button onClick={startNew} style={btnPrimary}>+ Add L{level} Rate</button>
      </div>

      {/* ── Level tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
        {[1, 2, 3].map(n => (
          <button key={n} onClick={() => { setLevel(n); setDraft(null); }}
            style={{ ...tabBtn, ...(level === n ? tabActive : {}) }}>
            Level {n}
            <span style={{ marginLeft: 8, fontSize: 11, color: '#64748B' }}>{LEVEL_HINT[n]}</span>
          </button>
        ))}
      </div>

      <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 14, lineHeight: 1.5 }}>
        Rates are picked up at simulation, contract, and invoicing time using a strict three-table priority lookup.
        Level 1 is the most specific; the resolver only falls to Level 2 if no Level 1 row matches, and to Level 3 if no Level 2 row matches.
        If still no match, the component is treated as <strong>0%</strong> and rendered red with a "No masters found" warning.
      </div>

      {/* ── Edit drawer ── */}
      {draft && (
        <div style={{ ...card, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#E5E7EB', marginBottom: 10 }}>
            {draft._new ? `New L${level} Rate` : `Edit L${level} Rate`}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            <Field label="Schema" required>
              <select value={draft.schema_id} onChange={e => set('schema_id', e.target.value)}
                disabled={!!lockedSchemaId} style={{ ...inp, opacity: lockedSchemaId ? 0.6 : 1 }}>
                <option value="">— Select schema —</option>
                {schemas.map(s => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
              </select>
            </Field>
            <Field label="Component" required>
              <select value={draft.component_id} onChange={e => set('component_id', e.target.value)}
                disabled={!draft.schema_id} style={inp}>
                <option value="">{draft.schema_id ? '— Select component —' : 'Pick a schema first'}</option>
                {components.map(c => <option key={c.id} value={c.id}>{c.code || c.name}</option>)}
              </select>
            </Field>
            <Field label="Rate %" required>
              <input type="number" step="0.001" value={draft.rate_pct}
                onChange={e => set('rate_pct', e.target.value)} style={inp} placeholder="e.g. 18" />
            </Field>

            {/* L1 only — jurisdiction. L1 + L2 — customer region. L3 — neither. */}
            {level === 1 && (
              <RegionPicker label="Jurisdiction (location region)" required
                value={draft.jurisdiction_region_id}
                onChange={(v) => set('jurisdiction_region_id', v)} placeholder="Select region..." />
            )}
            {level <= 2 && (
              <RegionPicker label="Customer Region" required
                value={draft.customer_region_id}
                onChange={(v) => set('customer_region_id', v)} placeholder="Select region..." />
            )}
            {level === 3 && <div></div>}
            {level !== 1 && <div></div>}

            <Field label="From Date" required>
              <input type="date" value={draft.from_date} onChange={e => set('from_date', e.target.value)} style={inp} />
            </Field>
            <Field label="To Date">
              <input type="date" value={draft.to_date} onChange={e => set('to_date', e.target.value)} style={inp} />
            </Field>
            <Field label="Notes">
              <input type="text" value={draft.notes} onChange={e => set('notes', e.target.value)} style={inp} placeholder="Optional" />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button onClick={cancelDraft} style={btnGhost}>Cancel</button>
            <button onClick={save} style={btnPrimary}>Save L{level} Rate</button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div style={card}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94A3B8' }}>Loading rates...</div>
        ) : filteredRates.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748B', fontSize: 13 }}>
            No L{level} rates yet. Click <strong>+ Add L{level} Rate</strong> to create the first one.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tbl}>
              <thead>
                <tr>
                  {!lockedSchemaId && <th style={th}>Schema</th>}
                  <th style={th}>Component</th>
                  {level === 1 && <th style={th}>Jurisdiction</th>}
                  {level <= 2 && <th style={th}>Customer Region</th>}
                  <th style={{ ...th, textAlign: 'right' }}>Rate %</th>
                  <th style={th}>From</th>
                  <th style={th}>To</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRates.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    {!lockedSchemaId && (
                      <td style={td}>
                        <div style={{ fontSize: 12.5 }}>{r.schema_code}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{r.schema_name}</div>
                      </td>
                    )}
                    <td style={td}>
                      <div style={{ fontSize: 12.5 }}>{r.component_name}</div>
                      {r.component_code && <div style={{ fontSize: 11, color: '#94A3B8' }}>{r.component_code}</div>}
                    </td>
                    {level === 1 && (
                      <td style={td}>{r.jurisdiction_country_name ? `${r.jurisdiction_country_name} · ${r.jurisdiction_region_name}` : '—'}</td>
                    )}
                    {level <= 2 && (
                      <td style={td}>{r.customer_country_name ? `${r.customer_country_name} · ${r.customer_region_name}` : '—'}</td>
                    )}
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{Number(r.rate_pct).toFixed(2)}%</td>
                    <td style={td}>{fmtDate(r.from_date)}</td>
                    <td style={td}>{r.to_date ? fmtDate(r.to_date) : <span style={{ color: '#64748B' }}>—</span>}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => startEdit(r)} style={btnSmall}>Edit</button>
                      <button onClick={() => remove(r)} style={{ ...btnSmall, color: '#EF4444', marginLeft: 6 }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const LEVEL_HINT = {
  1: 'jurisdiction + customer region',
  2: 'customer region only',
  3: 'fallback (no regions)',
};

function parseLockedSchemaId() {
  if (typeof window === 'undefined') return null;
  const m = (window.location.hash || '').match(/\/pricing-schemas\/([^/]+)\/component-masters/);
  if (!m) return null;
  if (m[1] === 'component-masters') return null;
  return m[1];
}
function fmtDate(v) {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}{required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 14 };
const inp  = { padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%' };
const btnPrimary = { padding: '8px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #FF00E5, #BF5FFF)', color: '#fff' };
const btnGhost   = { padding: '8px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };
const btnSmall   = { padding: '5px 10px', borderRadius: 5, fontSize: 11.5, fontWeight: 600, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };

const tabBtn    = { padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                    background: 'transparent', color: '#94A3B8', borderBottom: '2px solid transparent', marginBottom: -1 };
const tabActive = { color: '#FF00E5', borderBottom: '2px solid #FF00E5' };

const tbl = { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12.5 };
const th  = { padding: '10px 10px', textAlign: 'left', background: 'rgba(255,255,255,0.04)',
              color: '#94A3B8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px',
              borderBottom: '1px solid rgba(255,255,255,0.10)' };
const td  = { padding: '8px 10px', verticalAlign: 'middle' };
